import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Radio, FileText, ShieldCheck, Trophy, Plus, ArrowRight, AlertTriangle } from "lucide-react";
import { api, fmtDate, label } from "../../lib/api";
import { LIVE_STATUSES } from "../../lib/portals";
import { Panel, Empty, Spinner, Crest, StatusBadge } from "../../components/kit";
import { Stat } from "../referee/RefereeDashboard";
import { DemoLaunchCard } from "../DemoWalkthrough";

const PRE = ["scheduled", "awaiting_team_sheets", "teams_submitted", "teams_approved", "officials_confirmed", "ready_for_kickoff", "delayed"];
const REVIEW = ["full_time", "reports_pending", "reports_submitted", "under_review"];

const LiveCard = ({ m }) => (
  <Link to={`/matches/${m.id}`} data-testid={`league-live-${m.match_number}`} className="tro-card flex items-center gap-3 p-3 transition-colors hover:border-[#C5CBD2]">
    <Crest club={m.home} size={32} />
    <div className="min-w-0 flex-1 text-center"><div className="font-mono text-2xl font-extrabold leading-none">{m.score.home}–{m.score.away}</div><div className="mt-1 truncate text-[11px] font-bold text-[#6C757D]">{m.home.short} v {m.away.short} · {label(m.status)}</div></div>
    <Crest club={m.away} size={32} />
  </Link>
);

const Attention = ({ m, text, to, cta, testid }) => (
  <div className="flex items-center gap-3 rounded-[14px] border border-[#E7EAEE] p-2.5" data-testid={testid}>
    <AlertTriangle className="h-4 w-4 shrink-0 text-[#B38F00]" />
    <div className="min-w-0 flex-1"><div className="truncate text-sm font-bold">{m.match_number} · {m.home.short} v {m.away.short}</div><div className="truncate text-xs text-[#6C757D]">{text}</div></div>
    <Link to={to} className="shrink-0 rounded-[12px] bg-[#171B20] px-3 py-2 text-xs font-bold text-white">{cta}</Link>
  </div>
);

export default function LeagueDashboard() {
  const [d, setD] = useState(null);
  const [list, setList] = useState(null);
  const [comps, setComps] = useState([]);
  useEffect(() => {
    api.get("/dashboard/league").then((r) => setD(r.data));
    api.get("/matches", { params: { scope: "league" } }).then((r) => setList(r.data)).catch(() => setList([]));
    api.get("/competitions").then((r) => setComps(r.data)).catch(() => {});
  }, []);
  if (!d || !list) return <Spinner />;
  const upcoming = list.filter((m) => PRE.includes(m.status)).sort((a, b) => a.kickoff.localeCompare(b.kickoff));
  const live = list.filter((m) => LIVE_STATUSES.includes(m.status));
  const review = list.filter((m) => REVIEW.includes(m.status));
  const appointed = upcoming.filter((m) => m.officials?.referee);
  const noRef = upcoming.filter((m) => !m.officials?.referee);
  return (
    <div className="space-y-4" data-testid="league-dashboard">
      {list.some((m) => m.demo) && <DemoLaunchCard />}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><div className="tro-label">League Dashboard</div><h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">Competition control</h1></div>
        <div className="flex gap-2">
          <Link to="/league/fixtures?new=1" data-testid="league-new-fixture-btn" className="flex min-h-[44px] items-center gap-1.5 rounded-[14px] border border-[#E7EAEE] bg-white px-4 text-sm font-bold"><Plus className="h-4 w-4" />New fixture</Link>
          <Link to="/league/fixtures" data-testid="league-match-management-btn" className="flex min-h-[44px] items-center gap-1.5 rounded-[14px] bg-[#171B20] px-4 text-sm font-bold text-white">Match Management<ArrowRight className="h-4 w-4" /></Link>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
        <Stat icon={CalendarDays} l="Upcoming fixtures" v={upcoming.length} to="/league/fixtures?tab=upcoming" testid="league-stat-upcoming" />
        <Stat icon={Radio} l="Live fixtures" v={live.length} to="/league/fixtures?tab=live" testid="league-stat-live" />
        <Stat icon={FileText} l="Pending reports" v={review.length} to="/league/fixtures?tab=review" testid="league-stat-reports" />
        <Stat icon={ShieldCheck} l="Referees appointed" v={`${appointed.length}/${upcoming.length}`} to="/league/appointments" testid="league-stat-appointed" />
        <Stat icon={Trophy} l="Competitions" v={comps.length} to="/league/competitions" testid="league-stat-competitions" />
      </div>
      {live.length > 0 && <Panel title="Live now" testid="league-live-panel"><div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">{live.map((m) => <LiveCard key={m.id} m={m} />)}</div></Panel>}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel title="Needs attention" testid="league-attention">
          {!noRef.length && !review.length && <Empty text="Everything is on track." />}
          <div className="space-y-2">
            {review.map((m) => <Attention key={m.id} m={m} text={`${label(m.status)} · report review and result validation`} to={`/matches/${m.id}/reports`} cta="Review" testid={`attention-review-${m.match_number}`} />)}
            {noRef.map((m) => <Attention key={m.id} m={m} text={`No referee appointed · ${fmtDate(m.kickoff)}`} to="/league/appointments" cta="Assign" testid={`attention-noref-${m.match_number}`} />)}
          </div>
        </Panel>
        <Panel title="Upcoming fixtures" testid="league-upcoming">
          {!upcoming.length && <Empty text="No fixtures scheduled." />}
          <div className="space-y-2">{upcoming.slice(0, 5).map((m) => (
            <Link key={m.id} to={`/matches/${m.id}`} className="flex items-center gap-2 rounded-[14px] border border-[#E7EAEE] p-2.5 hover:bg-[#F7F8FA]" data-testid={`league-upcoming-${m.match_number}`}>
              <Crest club={m.home} size={28} /><div className="min-w-0 flex-1"><div className="truncate text-sm font-bold">{m.home.name} v {m.away.name}</div><div className="truncate text-xs text-[#6C757D]">{fmtDate(m.kickoff)} · {m.venue}</div></div><StatusBadge status={m.status} />
            </Link>
          ))}</div>
        </Panel>
      </div>
      <Panel title="Competition overview" testid="league-competitions">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">{comps.map((c) => {
          const ms = list.filter((m) => m.competition?.id === c.id || m.competition_id === c.id);
          return (
            <Link key={c.id} to="/league/standings" className="rounded-[14px] border border-[#E7EAEE] p-3 hover:bg-[#F7F8FA]" data-testid={`comp-overview-${c.id}`}>
              <div className="truncate font-bold">{c.name}</div><div className="text-xs text-[#6C757D]">{c.season} · {c.division} · {label(c.format)}</div>
              <div className="mt-2 flex gap-3 text-xs font-bold"><span>{ms.length} fixtures</span><span>{ms.filter((m) => m.status === "validated").length} validated</span></div>
            </Link>
          );
        })}</div>
        {!comps.length && <Empty text="No competitions yet." />}
      </Panel>
    </div>
  );
}
